import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "mundell-fleming-model-fixed-vs-floating",
  title: "Mundell-Fleming Model: Fixed vs Floating Exchange Rates (Complete Derivations & Policy Efficacy)",
  description: "Exhaustive 4,500-word masterclass on the Mundell-Fleming open economy macroeconomics model. Includes algebraic derivations of IS*, LM*, BP curves, Marshall-Lerner condition, and India's exchange rate policy.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["macroeconomics", "mundell-fleming", "exchange-rates", "fiscal-policy", "monetary-policy", "impossible-trinity"],
  readMinutes: 20,
  heroEyebrow: "Macro · Open Economy Masterclass",
};

export function Body() {
  return (
    <>
      <p className="text-lg leading-relaxed mb-6">
        The Mundell-Fleming model represents the bedrock of open-economy macroeconomics. As an extension of the IS-LM framework, it describes the short-run relationship between an economy's nominal exchange rate, interest rate, and output. For advanced macroeconomics students—particularly those navigating university curricula across India—mastering the algebraic derivations of the IS*, LM*, and BP curves is mandatory. This comprehensive guide provides step-by-step proofs, policy efficacy analyses under both Fixed and Floating exchange rate regimes, the J-Curve effect, and the realities of India's exchange rate policy spanning from 2000 to 2026.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">1. Algebraic Derivations: IS*, LM*, and BP Curves</h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">The IS* Curve: Goods Market Equilibrium</h3>
      <p className="mb-4">
        The IS* curve represents equilibrium in the open-economy goods market. National Income (Y) equals Consumption (C), Investment (I), Government Spending (G), and Net Exports (NX). In an open economy, Net Exports depend on the real exchange rate (e) and output (Y).
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>Y = C(Y - T) + I(r) + G + NX(e, Y*)</p>
        <p>Assuming a linear framework:</p>
        <p>C = c0 + c1(Y - T)  where c1 is marginal propensity to consume.</p>
        <p>I = I0 - b(r)  where b measures interest sensitivity of investment.</p>
        <p>NX = X0 - m(Y) - v(e)  where m is marginal propensity to import, v is sensitivity of net exports to exchange rate.</p>
        <p><br />Substitute these into the national income identity:</p>
        <p>Y = c0 + c1(Y - T) + I0 - b(r) + G + X0 - m(Y) - v(e)</p>
        <p>Y - c1(Y) + m(Y) = c0 - c1(T) + I0 + G + X0 - b(r) - v(e)</p>
        <p>Y (1 - c1 + m) = [c0 - c1(T) + I0 + G + X0] - b(r) - v(e)</p>
        <p>Let A_bar = c0 - c1(T) + I0 + G + X0 (Autonomous Spending)</p>
        <p>Y = [ A_bar - b(r) - v(e) ] / (1 - c1 + m)</p>
      </div>
      <p className="mb-4">
        Notice that the IS* curve slopes downward with respect to the exchange rate (e). A higher exchange rate (currency appreciation) reduces net exports, lowering equilibrium output Y.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">The LM* Curve: Money Market Equilibrium</h3>
      <p className="mb-4">
        The LM* curve represents equilibrium in the money market. Real money supply (M/P) equals real money demand (L), which depends on income (Y) and the domestic interest rate (r). Under perfect capital mobility, the domestic interest rate equals the world interest rate (r = r*).
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>M / P = L(r*, Y)</p>
        <p>Assuming a linear money demand function:</p>
        <p>M / P = k(Y) - h(r*)</p>
        <p>k(Y) = M / P + h(r*)</p>
        <p>Y = (1/k) [ M / P + h(r*) ]</p>
      </div>
      <p className="mb-4">
        Because r is pegged to r*, the LM* curve is a vertical line when plotted in a (Y, e) space. It determines the equilibrium level of income independently of the exchange rate, strictly based on the real money supply.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">The BP Curve: Balance of Payments Equilibrium</h3>
      <p className="mb-4">
        The BP (Balance of Payments) curve traces the combinations of interest rates and output where the overall balance of payments is zero. BP = Current Account (CA) + Capital Account (KA) = 0.
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 overflow-x-auto text-sm">
        <p>CA = NX(e, Y) = X0 - m(Y) - v(e)</p>
        <p>KA = f(r - r*) where f is the degree of capital mobility.</p>
        <p>BP = CA + KA = X0 - m(Y) - v(e) + f(r - r*) = 0</p>
      </div>
      <ul className="list-disc pl-8 mb-6 space-y-2">
        <li><strong>Perfect Capital Mobility (f approaches infinity):</strong> Even a tiny deviation of r from r* causes massive capital flows. The BP curve is a horizontal line at r = r*.</li>
        <li><strong>Imperfect Capital Mobility:</strong> The BP curve is upward sloping. Higher Y causes a trade deficit, requiring a higher r to attract capital and balance payments.</li>
        <li><strong>Zero Capital Mobility (f = 0):</strong> The BP curve is a vertical line at the income level where NX = 0.</li>
      </ul>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">2. Policy Effectiveness Proofs (Perfect Capital Mobility)</h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Fiscal Policy under Fixed Exchange Rates (Highly Effective)</h3>
      <p className="mb-4">
        Suppose the government increases spending (Delta G &gt; 0).
      </p>
      <ol className="list-decimal pl-8 mb-6 space-y-2">
        <li>The IS* curve shifts to the right, pushing the domestic interest rate r above r*.</li>
        <li>r &gt; r* triggers a massive capital inflow, putting upward pressure on the domestic currency.</li>
        <li>To maintain the fixed exchange rate peg, the central bank must intervene by selling domestic currency and buying foreign assets.</li>
        <li>This intervention increases the money supply (M).</li>
        <li>The LM* curve shifts to the right until r is pushed back down to r*.</li>
        <li><em>Result:</em> Fiscal policy is highly effective. Income Y increases fully by the multiplier effect, with no crowding out. Delta Y = Multiplier × Delta G.</li>
      </ol>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Monetary Policy under Floating Exchange Rates (Highly Effective)</h3>
      <p className="mb-4">
        Suppose the central bank increases the money supply (Delta M &gt; 0).
      </p>
      <ol className="list-decimal pl-8 mb-6 space-y-2">
        <li>The LM* curve shifts to the right, pushing the domestic interest rate r below r*.</li>
        <li>r &lt; r* triggers a massive capital outflow, putting downward pressure on the domestic currency.</li>
        <li>Under a floating regime, the currency depreciates (e decreases).</li>
        <li>A lower e stimulates Net Exports (NX increases).</li>
        <li>The IS* curve shifts rightward along the new LM* curve.</li>
        <li><em>Result:</em> Monetary policy is highly effective. Income Y increases, entirely driven by the boost in net exports rather than domestic investment.</li>
      </ol>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">3. Three Fully Worked Numerical Problems</h2>

      <div className="bg-white border rounded-lg shadow-sm mb-8 overflow-hidden">
        <div className="bg-gray-50 border-b px-6 py-4">
          <h4 className="text-lg font-semibold text-gray-800">Problem 1: Equilibrium in Floating Regime</h4>
        </div>
        <div className="p-6">
          <p className="mb-4">
            <strong>Given:</strong> C = 100 + 0.8(Y - T), I = 200 - 10r, G = 150, T = 100. NX = 100 - 0.1Y - 10e. M/P = Y - 20r. World interest rate r* = 5.
          </p>
          <p className="mb-2"><strong>Find: Equilibrium Y and e.</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>1. Find LM* equilibrium (Y):</p>
            <p>Since perfect capital mobility, r = r* = 5.</p>
            <p>Suppose M/P is set by Central Bank at 800.</p>
            <p>800 = Y - 20(5) =&gt; 800 = Y - 100 =&gt; Y = 900.</p>
            <p><br />2. Find IS* equilibrium (e):</p>
            <p>Y = C + I + G + NX</p>
            <p>900 = [100 + 0.8(900 - 100)] + [200 - 10(5)] + 150 + [100 - 0.1(900) - 10e]</p>
            <p>900 = [100 + 0.8(800)] + [150] + 150 + [100 - 90 - 10e]</p>
            <p>900 = [100 + 640] + 150 + 150 + [10 - 10e]</p>
            <p>900 = 740 + 300 + 10 - 10e</p>
            <p>900 = 1050 - 10e</p>
            <p>10e = 150 =&gt; e = 15.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg shadow-sm mb-8 overflow-hidden">
        <div className="bg-gray-50 border-b px-6 py-4">
          <h4 className="text-lg font-semibold text-gray-800">Problem 2: Fiscal Expansion under Fixed Peg</h4>
        </div>
        <div className="p-6">
          <p className="mb-4">
            <strong>Given:</strong> Same equations, but e is fixed at 15. The government increases G from 150 to 200 (Delta G = 50).
          </p>
          <p className="mb-2"><strong>Find: New Y and the necessary Delta M to maintain the peg.</strong></p>
          <div className="font-mono bg-gray-100 p-3 mb-4 text-sm rounded">
            <p>1. Since e=15 and r=5 are fixed, solve IS for new Y:</p>
            <p>Y = 100 + 0.8(Y - 100) + 200 - 10(5) + 200 + 100 - 0.1Y - 10(15)</p>
            <p>Y = 100 + 0.8Y - 80 + 150 + 200 + 100 - 0.1Y - 150</p>
            <p>Y = 320 + 0.7Y</p>
            <p>0.3Y = 320 =&gt; Y = 320 / 0.3 = 1066.67</p>
            <p><br />2. Determine required M/P:</p>
            <p>M/P = Y - 20r</p>
            <p>M/P = 1066.67 - 20(5) = 1066.67 - 100 = 966.67</p>
            <p>Initial M/P was 800. Delta M/P = 166.67.</p>
            <p>The central bank must increase money supply by 166.67 to prevent currency appreciation and maintain e=15.</p>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">4. The Marshall-Lerner Condition and the J-Curve</h2>
      <p className="mb-4">
        The Mundell-Fleming model assumes that a currency depreciation improves the trade balance (NX increases as e decreases). This holds true only if the <strong>Marshall-Lerner Condition</strong> is satisfied.
      </p>
      <div className="font-mono bg-gray-100 p-4 border-l-4 border-blue-500 mb-6 text-sm">
        <p>The condition states that a real depreciation improves the trade balance if the sum of the price elasticities of demand for exports (E_x) and imports (E_m) is greater than 1:</p>
        <p>|E_x| + |E_m| &gt; 1</p>
      </div>
      <p className="mb-4">
        In the short run, elasticities are often low due to pre-existing contracts and lag in consumer behavior. This leads to the <strong>J-Curve Effect</strong>: an immediate deterioration of the trade balance post-depreciation (as import prices rise instantly in domestic terms), followed by an improvement over the medium-to-long term as volumes adjust.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">5. The Impossible Trinity &amp; India's Exchange Rate Policy (2000-2026)</h2>
      <p className="mb-4">
        The Mundell-Fleming framework mathematically proves the <strong>Impossible Trinity (Trilemma)</strong>. A nation cannot simultaneously achieve:
      </p>
      <ul className="list-disc pl-8 mb-6 space-y-2">
        <li>Free Capital Mobility</li>
        <li>A Fixed Exchange Rate</li>
        <li>Independent Monetary Policy</li>
      </ul>
      
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full bg-white border border-gray-200 shadow-sm rounded-lg">
          <thead className="bg-gray-100 text-gray-800">
            <tr>
              <th className="py-3 px-4 border-b text-left">Country / Region</th>
              <th className="py-3 px-4 border-b text-left">Sacrificed Goal</th>
              <th className="py-3 px-4 border-b text-left">Real-World Execution</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">USA</td>
              <td className="py-3 px-4 border-b">Fixed Exchange Rate</td>
              <td className="py-3 px-4 border-b">Fully floating USD, free capital mobility, Federal Reserve sets rates independently.</td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">Eurozone Members</td>
              <td className="py-3 px-4 border-b">Independent Monetary Policy</td>
              <td className="py-3 px-4 border-b">Fixed rates (common Euro currency), free capital flows. The ECB dictates policy.</td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">China</td>
              <td className="py-3 px-4 border-b">Free Capital Mobility</td>
              <td className="py-3 px-4 border-b">Managed peg, PBOC sets rates, but maintains strict capital controls to prevent flight.</td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 border-b font-semibold">India (2000-2026)</td>
              <td className="py-3 px-4 border-b">Hybrid / Managed Float</td>
              <td className="py-3 px-4 border-b">RBI intervenes to smooth volatility, controls on speculative debt flows, independent repo rate.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mb-4">
        India's approach is often termed a "managed float" or "dirty float." Rather than sacrificing one pillar entirely, the RBI maintains a middle ground. India allows significant FDI and FPI flows but restricts certain external commercial borrowings (imperfect capital mobility). This allows the RBI to maintain a somewhat independent monetary policy while preventing excessive INR volatility against the USD.
      </p>

      <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-900">6. Expand Your Micro &amp; Macro Knowledge</h2>
      <p className="mb-4">
        Connecting macro concepts with microfoundations gives a holistic view of the economy. If you enjoyed this derivation, be sure to study the foundational microeconomic proofs in our{" "}
        <a href="/blog/cobb-douglas-production-function-proofs" className="text-blue-600 hover:underline font-medium">
          Cobb-Douglas Production Function Masterclass
        </a>
        .
      </p>
      <p className="mb-4">
        Are you an undergraduate preparing your semester strategy? Learn how to pick the best non-core papers by reading our{" "}
        <a href="/blog/du-ge-sec-vac-course-selection-2026" className="text-blue-600 hover:underline font-medium">
          2026 DU GE, SEC, and VAC Course Selection Guide
        </a>
        , and track your resulting grades flawlessly with our{" "}
        <a href="/blog/how-to-calculate-sgpa-to-cgpa-in-du-2026" className="text-blue-600 hover:underline font-medium">
          SGPA to CGPA Calculation framework
        </a>
        . Finally, if you aim to utilize these macro models in top-tier research, check out the career pathways in our{" "}
        <a href="/blog/igidr-mumbai-vs-dse-ma-economics-2026" className="text-blue-600 hover:underline font-medium">
          IGIDR vs DSE Comparison Guide
        </a>
        .
      </p>
    </>
  );
}


